import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Scenario, SceneElement, PhoneChatScene, PasswordGameScene, ScreenPosition } from '../types';
import { apiFetch } from '../systems/api-client';
import { PhoneChat } from './phone-chat';
import { PhoneScreenPosition } from './phone';
import { PasswordGame } from './password-game';
import styles from './admin-overlay-editor.module.css';
import { SHARED_ASSETS } from '../shared-assets';
import { getActiveWorkspaceHeaders } from '../systems/workspace-context';

interface AdminOverlayEditorProps {
  onBack: () => void;
}

export const AdminOverlayEditor: React.FC<AdminOverlayEditorProps> = ({ onBack }) => {
  const [scenarios, setScenarios] = useState<any[]>([]);
  const [selectedScenarioId, setSelectedScenarioId] = useState<string>('');
  const [scenario, setScenario] = useState<Scenario | null>(null);
  const [selectedSceneId, setSelectedSceneId] = useState<string>('');
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phoneScreenPos, setPhoneScreenPos] = useState<PhoneScreenPosition>({
    top: 12, left: 8, width: 84, height: 76,
  });
  const [pwGameScreenPos, setPwGameScreenPos] = useState<ScreenPosition>({
    top: 5, left: 17, width: 66, height: 58,
  });
  const previewRef = useRef<HTMLDivElement>(null);
  const [draggingElementId, setDraggingElementId] = useState<string | null>(null);

  useEffect(() => {
    fetchScenarios();
  }, []);

  const fetchScenarios = async () => {
    try {
      const response = await apiFetch('/api/auth/admin/scenario-catalog', {
        headers: {
          ...getActiveWorkspaceHeaders(),
        },
      });
      const data = await response.json();
      if (response.ok) setScenarios(data);
    } catch (err) {
      console.error('Error loading scenarios:', err);
    }
  };

  const loadScenario = async (id: string) => {
    setLoading(true);
    setSelectedScenarioId(id);
    try {
      const response = await apiFetch(`/api/admin/scenarios/${id}`, {
        headers: {
          ...getActiveWorkspaceHeaders(),
        },
      });
      const data = await response.json();
      if (response.ok) {
        setScenario(data);
        if (data.scenes.length > 0) {
          setSelectedSceneId(data.scenes[0].id);
        }
      }
    } catch (err) {
      console.error('Error loading scenario:', err);
    } finally {
      setLoading(false);
    }
  };

  const currentScene = scenario?.scenes.find(s => s.id === selectedSceneId);
  const selectedElement = currentScene?.elements?.find(e => e.id === selectedElementId);

  const passwordGameContent: PasswordGameScene | null =
    currentScene?.type === 'passwordGame' ? currentScene : null;

  useEffect(() => {
    if (passwordGameContent?.screenPosition) {
      setPwGameScreenPos(passwordGameContent.screenPosition);
    } else {
      setPwGameScreenPos({ top: 5, left: 17, width: 66, height: 58 });
    }
  }, [selectedSceneId, passwordGameContent?.screenPosition?.top, passwordGameContent?.screenPosition?.left, passwordGameContent?.screenPosition?.width, passwordGameContent?.screenPosition?.height]);

  const updatePasswordGameScreenPos = (updates: Partial<ScreenPosition>) => {
    if (!scenario || !selectedSceneId || !passwordGameContent) return;
    const newPos = { ...pwGameScreenPos, ...updates };
    setPwGameScreenPos(newPos);

    setScenario({
      ...scenario,
      scenes: scenario.scenes.map((s) =>
        s.id === selectedSceneId && s.type === 'passwordGame'
          ? { ...s, screenPosition: newPos }
          : s
      ),
    });
  };

  const updateElement = (elementId: string, updates: Partial<SceneElement>) => {
    if (!scenario || !selectedSceneId) return;

    const newScenario = { ...scenario };
    const scene = newScenario.scenes.find(s => s.id === selectedSceneId);
    if (scene && scene.elements) {
      scene.elements = scene.elements.map(e => 
        e.id === elementId ? { ...e, ...updates } : e
      );
      setScenario(newScenario);
    }
  };

  const updateElementPositionFromPointer = useCallback((elementId: string, clientX: number, clientY: number) => {
    const preview = previewRef.current;
    if (!preview || !selectedSceneId) return;

    const rect = preview.getBoundingClientRect();
    const x = Math.round(((clientX - rect.left) / rect.width) * 1000) / 10;
    const y = Math.round(((clientY - rect.top) / rect.height) * 1000) / 10;
    const clampedX = Math.min(100, Math.max(0, x));
    const clampedY = Math.min(100, Math.max(0, y));

    setScenario((prev) => {
      if (!prev) return prev;
      const scene = prev.scenes.find(s => s.id === selectedSceneId);
      if (!scene?.elements) return prev;

      return {
        ...prev,
        scenes: prev.scenes.map((sceneItem) => {
          if (sceneItem.id !== selectedSceneId || !sceneItem.elements) return sceneItem;
          return {
            ...sceneItem,
            elements: sceneItem.elements.map((element) => (
              element.id === elementId
                ? {
                    ...element,
                    position: {
                      ...element.position,
                      x: clampedX,
                      y: clampedY,
                    },
                  }
                : element
            )),
          };
        }),
      };
    });
  }, [selectedSceneId]);

  useEffect(() => {
    if (!draggingElementId) return;

    const handleMouseMove = (event: MouseEvent) => {
      updateElementPositionFromPointer(draggingElementId, event.clientX, event.clientY);
    };

    const handleMouseUp = () => {
      setDraggingElementId(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingElementId, updateElementPositionFromPointer]);

  const handleElementMouseDown = (event: React.MouseEvent, elementId: string) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedElementId(elementId);
    setDraggingElementId(elementId);
  };

  const saveChanges = async () => {
    if (!selectedScenarioId || !selectedSceneId || !currentScene) return;

    setIsSaving(true);
    try {
      const payload: Record<string, unknown> = {};
      if (currentScene.elements) payload.elements = currentScene.elements;
      if (currentScene.type === 'passwordGame' && currentScene.screenPosition) {
        payload.patch = { screenPosition: currentScene.screenPosition };
      }

      const response = await apiFetch(`/api/admin/scenarios/${selectedScenarioId}/scenes/${selectedSceneId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        alert('Changes saved successfully!');
      } else {
        const error = await response.json();
        alert(`Failed to save: ${error.message}`);
      }
    } catch (err) {
      console.error('Error saving changes:', err);
      alert('Error saving changes');
    } finally {
      setIsSaving(false);
    }
  };

  const getFullImageUrl = (path: string) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `/scenarios/${selectedScenarioId}/${path}`;
  };

  if (loading) return <div className={styles.loading}>Loading scenario...</div>;

  return (
    <div className={styles.adminEditor}>
      <header className={styles.header}>
        <button onClick={onBack} className={styles.backBtn}>← Back</button>
        <h1>Story Overlay Admin</h1>
        <div className={styles.controls}>
          <select 
            value={selectedScenarioId} 
            onChange={(e) => loadScenario(e.target.value)}
            className={styles.select}
          >
            <option value="">Select Scenario</option>
            {scenarios.map(s => (
              <option key={s.id} value={s.id}>{s.title}</option>
            ))}
          </select>

          {scenario && (
            <select 
              value={selectedSceneId} 
              onChange={(e) => setSelectedSceneId(e.target.value)}
              className={styles.select}
            >
              {scenario.scenes.map(s => (
                <option key={s.id} value={s.id}>{s.id}</option>
              ))}
            </select>
          )}

          <button 
            onClick={saveChanges} 
            disabled={isSaving || !scenario} 
            className={styles.saveBtn}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </header>

      <main className={styles.main}>
        {currentScene ? (
          <div className={styles.editorContainer}>
            <div className={styles.previewArea}>
              <div 
                ref={previewRef}
                className={styles.scenePreview}
                style={{ 
                  backgroundImage: currentScene.background ? `url(${getFullImageUrl(currentScene.background)})` : 'none'
                }}
              >
                {currentScene.elements?.map(element => {
                  const isCharacter = element.type === 'character';
                  const isDragging = draggingElementId === element.id;
                  return (
                    <div
                      key={element.id}
                      className={`${styles.element} ${isCharacter ? styles.characterElement : ''} ${selectedElementId === element.id ? styles.selected : ''} ${isDragging ? styles.dragging : ''}`}
                      style={{
                        left: `${element.position.x}%`,
                        top: isCharacter ? 'auto' : `${element.position.y}%`,
                        bottom: isCharacter ? '0' : undefined,
                        width: element.position.width ? `${element.position.width}%` : 'auto',
                        height: isCharacter ? '75%' : (element.position.height ? `${element.position.height}%` : 'auto'),
                        transform: isCharacter ? 'translateX(-50%)' : 'translate(-50%, -50%)',
                      }}
                      onMouseDown={(event) => handleElementMouseDown(event, element.id)}
                    >
                      {element.image && (
                        <img src={getFullImageUrl(element.image)} alt={element.id} />
                      )}
                      {element.text && <div className={styles.elementText}>{element.text}</div>}
                      <div className={styles.elementLabel}>{element.id}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className={styles.sidebar}>
              <h3>Elements</h3>
              {currentScene.elements?.some((element) => element.type === 'object') && (
                <p className={styles.hint}>
                  Glissez les objets sur l&apos;image pour les repositionner, puis enregistrez.
                </p>
              )}
              <div className={styles.elementList}>
                {currentScene.elements?.map(e => (
                  <button
                    key={e.id}
                    className={`${styles.elementItem} ${selectedElementId === e.id ? styles.active : ''}`}
                    onClick={() => setSelectedElementId(e.id)}
                  >
                    {e.id} ({e.type})
                  </button>
                ))}
              </div>

              {selectedElement && (
                <div className={styles.properties}>
                  <h3>Properties: {selectedElement.id}</h3>
                  <div className={styles.propGroup}>
                    <label>Position X (%)</label>
                    <input 
                      type="number" 
                      value={selectedElement.position.x} 
                      onChange={(e) => updateElement(selectedElement.id, {
                        position: { ...selectedElement.position, x: parseFloat(e.target.value) }
                      })}
                    />
                  </div>
                  <div className={styles.propGroup}>
                    <label>Position Y (%)</label>
                    <input 
                      type="number" 
                      value={selectedElement.position.y} 
                      onChange={(e) => updateElement(selectedElement.id, {
                        position: { ...selectedElement.position, y: parseFloat(e.target.value) }
                      })}
                    />
                  </div>
                  <div className={styles.propGroup}>
                    <label>Width (%)</label>
                    <input 
                      type="number" 
                      value={selectedElement.position.width || 0} 
                      onChange={(e) => updateElement(selectedElement.id, {
                        position: { ...selectedElement.position, width: parseFloat(e.target.value) }
                      })}
                    />
                  </div>
                  <div className={styles.propGroup}>
                    <label>Height (%)</label>
                    <input 
                      type="number" 
                      value={selectedElement.position.height || 0} 
                      onChange={(e) => updateElement(selectedElement.id, {
                        position: { ...selectedElement.position, height: parseFloat(e.target.value) }
                      })}
                    />
                  </div>
                </div>
              )}

              {/* Phone Screen Position Editor */}
              {(() => {
                const phoneChats: PhoneChatScene[] =
                  currentScene.type === 'phoneChat' ? [currentScene] : [];
                if (phoneChats.length === 0) return null;

                const phoneImgUrl = SHARED_ASSETS.telephone;

                return (
                  <div className={styles.properties}>
                    <h3>Phone Screen Position</h3>
                    <p style={{ fontSize: '11px', color: '#888', margin: '0 0 8px' }}>
                      Adjust where the chat content sits on the phone image (values in %).
                    </p>

                    {/* Visual preview */}
                    <div style={{
                      position: 'relative',
                      width: '100%',
                      maxWidth: '220px',
                      margin: '0 auto 12px',
                      background: '#000',
                      borderRadius: '8px',
                      overflow: 'hidden',
                    }}>
                      <img
                        src={phoneImgUrl}
                        alt="Phone"
                        style={{ width: '100%', display: 'block' }}
                      />
                      {/* Content overlay rectangle */}
                      <div style={{
                        position: 'absolute',
                        top: `${phoneScreenPos.top}%`,
                        left: `${phoneScreenPos.left}%`,
                        width: `${phoneScreenPos.width}%`,
                        height: `${phoneScreenPos.height}%`,
                        border: '2px dashed #00ff88',
                        background: 'rgba(0, 255, 136, 0.1)',
                        borderRadius: '4px',
                        pointerEvents: 'none',
                      }} />
                    </div>

                    {/* Number inputs */}
                    <div className={styles.propGroup}>
                      <label>Top (%)</label>
                      <input
                        type="number"
                        step={0.5}
                        value={phoneScreenPos.top}
                        onChange={(e) => setPhoneScreenPos(p => ({ ...p, top: parseFloat(e.target.value) || 0 }))}
                      />
                    </div>
                    <div className={styles.propGroup}>
                      <label>Left (%)</label>
                      <input
                        type="number"
                        step={0.5}
                        value={phoneScreenPos.left}
                        onChange={(e) => setPhoneScreenPos(p => ({ ...p, left: parseFloat(e.target.value) || 0 }))}
                      />
                    </div>
                    <div className={styles.propGroup}>
                      <label>Width (%)</label>
                      <input
                        type="number"
                        step={0.5}
                        value={phoneScreenPos.width}
                        onChange={(e) => setPhoneScreenPos(p => ({ ...p, width: parseFloat(e.target.value) || 0 }))}
                      />
                    </div>
                    <div className={styles.propGroup}>
                      <label>Height (%)</label>
                      <input
                        type="number"
                        step={0.5}
                        value={phoneScreenPos.height}
                        onChange={(e) => setPhoneScreenPos(p => ({ ...p, height: parseFloat(e.target.value) || 0 }))}
                      />
                    </div>

                    <div style={{
                      marginTop: '8px',
                      padding: '6px',
                      background: '#1a1a2e',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontFamily: 'monospace',
                      color: '#0f0',
                    }}>
                      screenPosition={'{'} top: {phoneScreenPos.top}, left: {phoneScreenPos.left}, width: {phoneScreenPos.width}, height: {phoneScreenPos.height} {'}'}
                    </div>

                    {/* Chat preview inside the phone */}
                    <h3 style={{ marginTop: '16px' }}>Chat Preview</h3>
                    {phoneChats.map((chat, idx) => (
                      <div key={idx} style={{
                        position: 'relative',
                        width: '100%',
                        maxWidth: '220px',
                        margin: '0 auto 12px',
                        background: '#000',
                        borderRadius: '8px',
                        overflow: 'hidden',
                      }}>
                        <img
                          src={phoneImgUrl}
                          alt="Phone"
                          style={{ width: '100%', display: 'block' }}
                        />
                        <div style={{
                          position: 'absolute',
                          top: `${phoneScreenPos.top}%`,
                          left: `${phoneScreenPos.left}%`,
                          width: `${phoneScreenPos.width}%`,
                          height: `${phoneScreenPos.height}%`,
                          borderRadius: '4px',
                          overflow: 'hidden',
                        }}>
                          <PhoneChat
                            contactName={chat.contactName}
                            messages={chat.messages}
                            previewMode={true}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Password Game Screen Position Editor */}
              {passwordGameContent && (() => {
                const bgUrl = currentScene?.background && selectedScenarioId
                  ? `/scenarios/${selectedScenarioId}/${currentScene.background}`
                  : '';

                return (
                  <div className={styles.properties}>
                    <h3>Password Game Screen Position</h3>
                    <p style={{ fontSize: '11px', color: '#888', margin: '0 0 8px' }}>
                      Adjust where the 3-panel game sits on the laptop screen (values in %).
                    </p>

                    {/* Visual preview */}
                    <div style={{
                      position: 'relative',
                      width: '100%',
                      margin: '0 auto 12px',
                      background: '#222',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      aspectRatio: '16/9',
                    }}>
                      {bgUrl && (
                        <img
                          src={bgUrl}
                          alt="Background"
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      )}
                      <div style={{
                        position: 'absolute',
                        top: `${pwGameScreenPos.top}%`,
                        left: `${pwGameScreenPos.left}%`,
                        width: `${pwGameScreenPos.width}%`,
                        height: `${pwGameScreenPos.height}%`,
                        border: '2px dashed #00ff88',
                        background: 'rgba(0, 255, 136, 0.1)',
                        borderRadius: '4px',
                        pointerEvents: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '2%',
                        padding: '1%',
                      }}>
                        <div style={{ flex: '0 0 28%', background: 'rgba(255,255,255,0.3)', borderRadius: '4px', height: '80%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', color: '#fff' }}>Profil</div>
                        <div style={{ flex: 1, background: 'rgba(255,255,255,0.2)', borderRadius: '4px', height: '80%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', color: '#fff' }}>Mot de passe</div>
                        <div style={{ flex: '0 0 28%', background: 'rgba(255,255,255,0.3)', borderRadius: '4px', height: '80%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', color: '#fff' }}>Eval</div>
                      </div>
                    </div>

                    <div className={styles.propGroup}>
                      <label>Top (%)</label>
                      <input
                        type="number"
                        step={0.5}
                        value={pwGameScreenPos.top}
                        onChange={(e) => updatePasswordGameScreenPos({ top: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className={styles.propGroup}>
                      <label>Left (%)</label>
                      <input
                        type="number"
                        step={0.5}
                        value={pwGameScreenPos.left}
                        onChange={(e) => updatePasswordGameScreenPos({ left: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className={styles.propGroup}>
                      <label>Width (%)</label>
                      <input
                        type="number"
                        step={0.5}
                        value={pwGameScreenPos.width}
                        onChange={(e) => updatePasswordGameScreenPos({ width: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className={styles.propGroup}>
                      <label>Height (%)</label>
                      <input
                        type="number"
                        step={0.5}
                        value={pwGameScreenPos.height}
                        onChange={(e) => updatePasswordGameScreenPos({ height: parseFloat(e.target.value) || 0 })}
                      />
                    </div>

                    <div style={{
                      marginTop: '8px',
                      padding: '6px',
                      background: '#1a1a2e',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontFamily: 'monospace',
                      color: '#0f0',
                    }}>
                      screenPosition={'{'} top: {pwGameScreenPos.top}, left: {pwGameScreenPos.left}, width: {pwGameScreenPos.width}, height: {pwGameScreenPos.height} {'}'}
                    </div>

                    {/* Live preview on background */}
                    <h3 style={{ marginTop: '16px' }}>Game Preview</h3>
                    <div style={{
                      position: 'relative',
                      width: '100%',
                      margin: '0 auto 12px',
                      background: '#222',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      aspectRatio: '16/9',
                    }}>
                      {bgUrl && (
                        <img
                          src={bgUrl}
                          alt="Background"
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      )}
                      <div style={{
                        position: 'absolute',
                        top: `${pwGameScreenPos.top}%`,
                        left: `${pwGameScreenPos.left}%`,
                        width: `${pwGameScreenPos.width}%`,
                        height: `${pwGameScreenPos.height}%`,
                        borderRadius: '4px',
                        overflow: 'hidden',
                      }}>
                        <PasswordGame
                          rounds={passwordGameContent.rounds}
                          victoryReward={passwordGameContent.victoryReward}
                          onComplete={() => {}}
                        />
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        ) : (
          <div className={styles.noScene}>Select a scenario and scene to begin editing.</div>
        )}
      </main>
    </div>
  );
};
